import { useState } from "react";
import { INPUT_TYPE } from "../engine/indicators/indicatorTypes";
import {
  createDraft,
  draftToChanges,
  FREE_LEVEL_RULES,
  filterNumberText,
  levelDefault,
  normalizeNumberInput,
} from "../components/IndicatorSettingsModal/indicatorDraft";

/**
 * Inputs and styles of an indicator edited in its settings modal (TV-17).
 * The chart does not change until Save: the draft lives only while the
 * modal is open, so Cancel just drops it.
 *
 * @param {object} indicator - engine.getIndicators() item
 * @param {object} definition - from the registry
 * @returns {{
 *   draft: { inputs: object, plots: object, levels: object[] },
 *   setInput: (input: object, value: string) => void,
 *   commitInput: (input: object) => void,
 *   setPlot: (key: string, change: object) => void,
 *   setLevel: (index: number, change: object) => void,
 *   commitLevel: (index: number) => void,
 *   getChanges: () => { inputs: object, styles: object },
 * }}
 */
const useIndicatorDraft = (indicator, definition) => {
  const [draft, setDraft] = useState(() => createDraft(indicator, definition));

  const { levelRules } = definition;

  // A number field keeps only what PO lets type in it
  const setInput = (input, value) => {
    const text = input.type === INPUT_TYPE.NUMBER ? filterNumberText(input, value) : value;

    setDraft((current) => ({ ...current, inputs: { ...current.inputs, [input.key]: text } }));
  };

  // A number field shows what will be saved once it loses focus
  const commitInput = (input) => {
    setDraft((current) => {
      const value = normalizeNumberInput(input, current.inputs[input.key], input.defaultValue);

      return { ...current, inputs: { ...current.inputs, [input.key]: String(value) } };
    });
  };

  const setPlot = (key, change) => {
    setDraft((current) => ({
      ...current,
      plots: { ...current.plots, [key]: { ...current.plots[key], ...change } },
    }));
  };

  const updateLevel = (index, update) => {
    setDraft((current) => ({
      ...current,
      levels: current.levels.map((level, levelIndex) => (levelIndex === index ? update(level) : level)),
    }));
  };

  const setLevel = (index, change) => {
    const rules = levelRules || FREE_LEVEL_RULES;
    const filtered = change.value === undefined ? change : { ...change, value: filterNumberText(rules, change.value) };

    updateLevel(index, (level) => ({ ...level, ...filtered }));
  };

  // A limited level shows what will be saved once its field loses focus; other levels stay as typed
  const commitLevel = (index) => {
    if (!levelRules) return;

    updateLevel(index, (level) => ({
      ...level,
      value: String(normalizeNumberInput(levelRules, level.value, levelDefault(definition, index))),
    }));
  };

  const getChanges = () => draftToChanges(draft, indicator, definition);

  return { draft, setInput, commitInput, setPlot, setLevel, commitLevel, getChanges };
};

export default useIndicatorDraft;
